import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';
import { formatMoney } from '@theme/money-formatting';

/**
 * 2-step pair: variant pills update pair price; CTA adds both selected variants.
 * Instance-scoped — queries stay on this element.
 */
class MgTwoStepPair extends HTMLElement {
  /** @type {boolean} */
  #bound = false;

  /** @type {boolean} */
  #pending = false;

  connectedCallback() {
    if (this.#bound) return;
    this.#bound = true;
    this.addEventListener('click', this.#onClick);
    this.#sync();
    requestAnimationFrame(() => this.#sync());
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
  }

  get #config() {
    const script = this.querySelector('[data-mg-tsp-config]');
    if (!script) return {};
    try {
      const parsed = JSON.parse(script.textContent || '{}');
      return parsed && typeof parsed === 'object' ? parsed : {};
    } catch {
      return {};
    }
  }

  get #moneyFormat() {
    return this.#config.moneyFormat || this.dataset.moneyFormat || '${{amount}}';
  }

  get #currency() {
    return this.#config.currency || this.dataset.currency || 'USD';
  }

  get #discountPercent() {
    const value = Number(this.dataset.discountPercent);
    return Number.isFinite(value) ? Math.max(0, Math.min(100, value)) : 0;
  }

  get #savingsTemplate() {
    return this.dataset.savingsTemplate || '';
  }

  get #errorMessage() {
    return this.dataset.errorMessage || '';
  }

  /** @param {Event} event */
  #onClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const pill = target.closest('[data-mg-tsp-pill]');
    if (pill instanceof HTMLButtonElement && this.contains(pill)) {
      event.preventDefault();
      if (pill.disabled) return;
      this.#selectPill(pill);
      return;
    }

    const cta = target.closest('[data-mg-tsp-cta]');
    if (cta instanceof HTMLButtonElement && this.contains(cta)) {
      event.preventDefault();
      this.#addPair(cta);
    }
  };

  /** @param {HTMLButtonElement} pill */
  #selectPill(pill) {
    const card = pill.closest('[data-mg-tsp-product]');
    if (!(card instanceof HTMLElement)) return;

    const optionIndex = Number(pill.dataset.optionIndex);
    const value = pill.dataset.value || '';
    const group = card.querySelector(`[data-mg-tsp-option-group="${optionIndex}"]`);
    group?.querySelectorAll('[data-mg-tsp-pill]').forEach((button) => {
      button.setAttribute('aria-pressed', button === pill ? 'true' : 'false');
    });

    const selected = this.#readSelectedOptions(card);
    selected[optionIndex] = value;
    card.dataset.selectedOptions = JSON.stringify(selected);

      const variant = this.#findVariant(this.#readVariants(card), selected, card.dataset.variantId);
      this.#applyVariant(card, variant);
    this.#sync();
  }

  /**
   * @param {HTMLElement} card
   * @returns {string[]}
   */
  #readSelectedOptions(card) {
    const fromPills = [];
    let pillCount = 0;
    card.querySelectorAll('[data-mg-tsp-option-group]').forEach((group) => {
      const index = Number(group.getAttribute('data-mg-tsp-option-group'));
      if (!Number.isFinite(index)) return;
      const pressed = group.querySelector('[data-mg-tsp-pill][aria-pressed="true"]');
      if (!(pressed instanceof HTMLElement)) return;
      pillCount += 1;
      fromPills[index] = (pressed.dataset.value || '').trim();
    });
    if (pillCount > 0) {
      const length = fromPills.length;
      return Array.from({ length }, (_, index) => fromPills[index] || '');
    }

    try {
      const parsed = JSON.parse(card.dataset.selectedOptions || '[]');
      return Array.isArray(parsed) ? parsed.map((value) => String(value).trim()) : [];
    } catch {
      return [];
    }
  }

  /**
   * @param {HTMLElement} card
   * @returns {Array<{id: number, available: boolean, price: number, options: string[], featured_image: string | null}>}
   */
  #readVariants(card) {
    const script = card.querySelector('[data-mg-tsp-variants]');
    if (!script) return [];
    try {
      const parsed = JSON.parse(script.textContent || '[]');
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }

  /**
   * @param {Array<{id: number, available: boolean, options: string[]}>} variants
   * @param {string[]} selected
   * @param {string} [fallbackId]
   */
  #findVariant(variants, selected, fallbackId) {
    const normalized = selected.map((value) => String(value).trim());
    const match = variants.find((variant) => {
      if (!Array.isArray(variant.options)) return false;
      if (normalized.length > 0 && variant.options.length !== normalized.length) return false;
      if (normalized.length === 0) return false;
      return variant.options.every((option, index) => String(option).trim() === normalized[index]);
    });
    if (match) return match;

    if (fallbackId) {
      const byId = variants.find((variant) => String(variant.id) === String(fallbackId));
      if (byId) return byId;
    }

    return variants.find((variant) => variant.available) || variants[0] || null;
  }

  /**
   * @param {number} cents
   * @returns {string}
   */
  #formatPairMoney(cents) {
    const formatted = formatMoney(cents, this.#moneyFormat, this.#currency);
    // Magento pair CTA uses whole dollars without trailing .00
    return formatted.replace(/([.,]00)(?!\d)/, '');
  }

  /**
   * @param {HTMLElement} card
   * @param {{id: number, available: boolean, price: number, featured_image: string | null} | null} variant
   */
  #applyVariant(card, variant) {
    const priceEl = card.querySelector('[data-mg-tsp-price]');
    if (priceEl && variant) {
      priceEl.textContent = formatMoney(variant.price, this.#moneyFormat, this.#currency);
    }

    card.dataset.variantId = variant ? String(variant.id) : '';
    card.dataset.available = variant && variant.available ? 'true' : 'false';

    if (card.dataset.lockMedia === 'true' || !variant?.featured_image) return;

    const productId = card.dataset.productId;
    if (!productId) return;

    const media = this.querySelector(`[data-mg-tsp-product-media][data-product-id="${productId}"]`);
    if (!(media instanceof HTMLElement) || media.dataset.lockMedia === 'true') return;

    media.querySelectorAll('img').forEach((image) => {
      if (image instanceof HTMLImageElement) image.src = variant.featured_image || image.src;
    });
  }

  #productCards() {
    return Array.from(this.querySelectorAll('[data-mg-tsp-product]')).filter(
      (card) => card instanceof HTMLElement && card.dataset.hasProduct === 'true'
    );
  }

  #sync() {
    const cards = this.#productCards();
    let sum = 0;
    let allAvailable = cards.length > 0;

    for (const card of cards) {
      if (!(card instanceof HTMLElement)) continue;
      const selected = this.#readSelectedOptions(card);
      const variants = this.#readVariants(card);
      const variant = this.#findVariant(variants, selected, card.dataset.variantId);
      this.#updatePillAvailability(card, variants, selected);

      if (!variant) {
        allAvailable = false;
        continue;
      }

      if (!variant.available) {
        allAvailable = false;
      }

      sum += Number(variant.price) || 0;
      this.#applyVariant(card, variant);
    }

    const discount = this.#discountPercent;
    const display = Math.round((sum * (100 - discount)) / 100);
    const savings = Math.max(0, sum - display);

    const priceEl = this.querySelector('[data-mg-tsp-pair-price]');
    if (priceEl) {
      priceEl.textContent = this.#formatPairMoney(display);
    }

    const savingsEl = this.querySelector('[data-mg-tsp-savings]');
    if (savingsEl instanceof HTMLElement) {
      if (!this.#savingsTemplate || discount <= 0 || savings <= 0) {
        savingsEl.hidden = true;
      } else {
        savingsEl.hidden = false;
        savingsEl.textContent = this.#savingsTemplate
          .split('[percent]')
          .join(String(discount))
          .split('[amount]')
          .join(this.#formatPairMoney(savings));
      }
    }

    const cta = this.querySelector('[data-mg-tsp-cta]');
    if (cta instanceof HTMLButtonElement) {
      const disabled = this.#pending || !allAvailable || cards.length === 0;
      cta.disabled = disabled;
      cta.setAttribute('aria-disabled', disabled ? 'true' : 'false');
    }
  }

  /**
   * @param {HTMLElement} card
   * @param {Array<{available: boolean, options: string[]}>} variants
   * @param {string[]} selected
   */
  #updatePillAvailability(card, variants, selected) {
    card.querySelectorAll('[data-mg-tsp-pill]').forEach((pill) => {
      if (!(pill instanceof HTMLButtonElement)) return;
      const optionIndex = Number(pill.dataset.optionIndex);
      const value = pill.dataset.value || '';
      const available = variants.some((variant) => {
        if (!variant.available || variant.options[optionIndex] !== value) return false;
        return variant.options.every((option, index) => index === optionIndex || option === selected[index]);
      });
      pill.disabled = !available;
    });
  }

  /** @param {HTMLButtonElement} cta */
  async #addPair(cta) {
    if (this.#pending || cta.disabled) return;

    const cards = this.#productCards();
    const items = [];

    for (const card of cards) {
      if (!(card instanceof HTMLElement)) continue;
      const id = Number(card.dataset.variantId);
      if (!id || card.dataset.available !== 'true') {
        this.#showError(this.#errorMessage);
        return;
      }
      items.push({ id, quantity: 1 });
    }

    if (items.length === 0) return;

    this.#pending = true;
    this.#showError('');
    cta.classList.add('is-pending');
    cta.setAttribute('aria-busy', 'true');
    this.#sync();

    const deferredEventPromise = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: items.map((item) => ({
          merchandiseId: String(item.id),
          quantity: item.quantity,
        })),
        promise: deferredEventPromise.promise,
      })
    );

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({ items }),
      });
      const payload = await response.json();

      if (payload.status) {
        this.dispatchEvent(
          new CartErrorEvent({
            error: payload.message || this.#errorMessage,
            code: 'INVALID',
            detail: {
              description: payload.description,
              errors: payload.errors,
            },
          })
        );
        const cart = await this.#refreshCart();
        deferredEventPromise.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
          detail: {
            didError: true,
            items: cart.items,
            source: 'mg-two-step-pair',
            sourceId: this.id || this.dataset.sectionId || '',
            itemCount: items.length,
          },
        });
        this.#showError(payload.description || payload.message || this.#errorMessage);
        return;
      }

      const cart = await this.#refreshCart();
      deferredEventPromise.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          source: 'mg-two-step-pair',
          sourceId: this.id || this.dataset.sectionId || '',
          itemCount: items.length,
          didError: false,
        },
      });

      const drawer = document.querySelector('theme-drawer#cart-drawer');
      if (drawer && typeof drawer.open === 'function' && !drawer.hasAttribute('open')) {
        drawer.open();
      }
    } catch (error) {
      deferredEventPromise.reject(error);
      this.dispatchEvent(
        new CartErrorEvent({
          error: error?.message || this.#errorMessage,
          code: 'SERVICE_UNAVAILABLE',
        })
      );
      this.#showError(this.#errorMessage);
    } finally {
      this.#pending = false;
      cta.classList.remove('is-pending');
      cta.removeAttribute('aria-busy');
      this.#sync();
    }
  }

  async #refreshCart() {
    const cartItemsComponent = document.querySelector('cart-items-component');
    if (cartItemsComponent && typeof cartItemsComponent.fetchCartData === 'function') {
      await customElements.whenDefined('cart-items-component');
      return cartItemsComponent.fetchCartData();
    }

    const response = await fetch(`${Theme.routes.cart_url}.json`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    });
    if (!response.ok) throw new Error(`Failed to fetch cart: ${response.status}`);
    return response.json();
  }

  /** @param {string} message */
  #showError(message) {
    const errorEl = this.querySelector('[data-mg-tsp-error]');
    if (!(errorEl instanceof HTMLElement)) return;
    if (!message) {
      errorEl.hidden = true;
      errorEl.textContent = '';
      return;
    }
    errorEl.hidden = false;
    errorEl.textContent = message;
  }
}

if (!customElements.get('mg-two-step-pair')) {
  customElements.define('mg-two-step-pair', MgTwoStepPair);
}
